/**
 * /payroll/year-end/941/:year/:quarter — Quarterly Form 941 view + PDF.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, Box, Button, Card, CardContent, IconButton, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { api, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/year-end_/941/$year/$quarter")({
  component: Form941Page,
});

interface Deposit {
  id: string; year: number; quarter: number | null; deposit_date: string;
  amount: string; payment_method: string; eftps_confirmation: string | null;
}

const fmtMoney = (s: string | number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(s));

function Form941Page() {
  const { year, quarter } = Route.useParams();
  const yr = Number(year);
  const q = Number(quarter);

  const { data: deposits = [] } = useQuery({
    queryKey: ["payroll", "tax-deposits", yr, q],
    queryFn: async () => (await api(`/api/payroll/year-end/deposits?year=${yr}&quarter=${q}&form_kind=941`)) as Deposit[],
  });

  const totalDeposited = deposits.reduce((sum, d) => sum + Number(d.amount), 0);

  const downloadPdf = () => {
    const url = `/api/payroll/year-end/941/${yr}/q${q}.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `Form-941-${yr}-Q${q}.pdf`; a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton component={Link} to="/payroll/year-end" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">Back to Year-End</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Form 941 — Q{q} {yr}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Employer's Quarterly Federal Tax Return — wages, FIT, FICA reconciliation.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-year-end/filing-941.md" label="Form 941 filing guide" />
          </Box>
        </Box>
        <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}>
          Generate PDF
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={1}>Quarterly Tax Deposits</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These deposits flow to <strong>Form 941 line 13a</strong>. Add new deposits via the{" "}
            <Link to="/payroll/tax-deposits">Tax Deposits</Link> page.
          </Typography>
          <Stack direction="row" spacing={3} mb={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total deposited Q{q}</Typography>
              <Typography variant="h5" fontWeight={600}>{fmtMoney(totalDeposited)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Number of deposits</Typography>
              <Typography variant="h5" fontWeight={600}>{deposits.length}</Typography>
            </Box>
          </Stack>
          {deposits.length > 0 ? (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>EFTPS Confirmation</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deposits.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.deposit_date}</TableCell>
                      <TableCell>{d.payment_method}</TableCell>
                      <TableCell>{d.eftps_confirmation || "—"}</TableCell>
                      <TableCell align="right">{fmtMoney(d.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Alert severity="info">
              No deposits recorded for Q{q}. The 941 will show full balance due.{" "}
              <Link to="/payroll/tax-deposits">Add a deposit</Link>.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
